/**
 * Data access for the product-order feature (quote + create).
 *
 * - ``POST /api/products/<id>/quote/`` — server-side price preview (no side effects).
 * - ``POST /api/orders/``             — atomic + idempotent single-product order.
 */
import { apiClient } from "@shared/api";
import type { OrderDetail } from "@entities/order";

import type { CreateOrderInput, ProductQuote, QuoteInput } from "./model";

/** Path of a product's server-side quote endpoint. */
export function productQuotePath(productId: number): string {
  return `/products/${productId}/quote/`;
}

/** Order-create endpoint (one product per order). */
export const ORDERS_PATH = "/orders/";

/** Request a server-side price preview for a product (no side effects). */
export function quoteProduct(
  productId: number,
  input: QuoteInput,
): Promise<ProductQuote> {
  return apiClient.post<ProductQuote, QuoteInput>(productQuotePath(productId), {
    body: input,
  });
}

/**
 * Create an order for a single product. The ``idempotencyKey`` makes a retried or
 * double-tapped submit safe (the server returns the already-created order rather than
 * charging twice).
 */
export function createProductOrder(
  input: CreateOrderInput,
  idempotencyKey: string,
): Promise<OrderDetail> {
  return apiClient.post<OrderDetail, CreateOrderInput>(ORDERS_PATH, {
    body: input,
    idempotencyKey,
  });
}
